import { useCallback, useEffect, useRef, useState } from 'react';

const SWIPE_THRESHOLD = 100;
const UP_SWIPE_THRESHOLD = 90;
const TAP_MAX_DRIFT = 6;
const HAPTIC_MS = 30;

export type SwipeDirection = 'left' | 'right' | null;

export interface UseSwipeGestureArgs {
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
  /** „Super-Swipe" nach oben — z.B. Inserat öffnen; Objekt bleibt in der Queue. Optional. */
  onSwipeUp?: () => void;
  /** Called for a true tap (no drag > TAP_MAX_DRIFT). Optional. */
  onTap?: () => void;
  /** Add keyboard ←→ shortcuts. Default true. */
  enableKeyboard?: boolean;
}

export interface UseSwipeGestureResult {
  dragX: number;
  dragY: number;
  isDragging: boolean;
  committedExit: SwipeDirection;
  handlers: {
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerUp: (e: React.PointerEvent<HTMLDivElement>) => void;
    onPointerCancel: (e: React.PointerEvent<HTMLDivElement>) => void;
  };
  rootRef: React.RefObject<HTMLDivElement>;
  /** Returns inline style fragment for the dragged element. */
  style: React.CSSProperties;
}

function tryHaptic(ms = HAPTIC_MS): void {
  try {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      navigator.vibrate(ms);
    }
  } catch {
    // ignore
  }
}

export function useSwipeGesture(args: UseSwipeGestureArgs): UseSwipeGestureResult {
  const { onSwipeLeft, onSwipeRight, onSwipeUp, onTap, enableKeyboard = true } = args;
  const [dragX, setDragX] = useState(0);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [committedExit, setCommittedExit] = useState<SwipeDirection>(null);
  const startX = useRef(0);
  const startY = useRef(0);
  const movedDistance = useRef(0);
  const rootRef = useRef<HTMLDivElement>(null);

  const triggerLeft = useCallback((): void => {
    setCommittedExit('left');
    tryHaptic(50);
    setTimeout(() => onSwipeLeft(), 220);
  }, [onSwipeLeft]);

  const triggerRight = useCallback((): void => {
    setCommittedExit('right');
    tryHaptic(50);
    setTimeout(() => onSwipeRight(), 220);
  }, [onSwipeRight]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (committedExit) return;
      // Skip drag if target is interactive
      const target = e.target as HTMLElement;
      if (target.closest('a, button, [role="button"], input, textarea, select')) {
        return;
      }
      startX.current = e.clientX - dragX;
      startY.current = e.clientY - dragY;
      movedDistance.current = 0;
      setIsDragging(true);
      rootRef.current?.setPointerCapture(e.pointerId);
    },
    [dragX, dragY, committedExit],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!isDragging || committedExit) return;
      const nextX = e.clientX - startX.current;
      const nextY = e.clientY - startY.current;
      movedDistance.current = Math.max(movedDistance.current, Math.abs(nextX), Math.abs(nextY));
      setDragX(nextX);
      setDragY(nextY);
    },
    [isDragging, committedExit],
  );

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (committedExit) return;
      // Tap/Release auf einem interaktiven Kind (Herz, Links, Buttons) NICHT als Karten-Tap
      // werten. onPointerDown überspringt bei solchen Targets den Drag-Start (kein Pointer-
      // Capture) → e.target bleibt das Kind; bei echtem Drag ist e.target dagegen der captured
      // Root (nicht interaktiv). stopPropagation am Click käme zu spät (Click folgt nach pointerup).
      const target = e.target as HTMLElement;
      if (target.closest('a, button, [role="button"], input, textarea, select')) {
        setIsDragging(false);
        try {
          rootRef.current?.releasePointerCapture(e.pointerId);
        } catch {
          // ignore
        }
        setDragX(0);
        setDragY(0);
        return;
      }
      setIsDragging(false);
      try {
        rootRef.current?.releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }
      // Nach oben (Super-Swipe): überwiegend vertikal-hoch → onSwipeUp, Karte bleibt (kein Exit).
      if (dragY < -UP_SWIPE_THRESHOLD && Math.abs(dragY) > Math.abs(dragX)) {
        tryHaptic(50);
        onSwipeUp?.();
        setDragX(0);
        setDragY(0);
        return;
      }
      if (dragX > SWIPE_THRESHOLD) {
        triggerRight();
      } else if (dragX < -SWIPE_THRESHOLD) {
        triggerLeft();
      } else {
        if (movedDistance.current < TAP_MAX_DRIFT && onTap) {
          onTap();
        }
        setDragX(0);
      }
      setDragY(0);
    },
    [dragX, dragY, committedExit, triggerLeft, triggerRight, onSwipeUp, onTap],
  );

  const onPointerCancel = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (committedExit) return;
      setIsDragging(false);
      try {
        rootRef.current?.releasePointerCapture(e.pointerId);
      } catch {
        // ignore
      }
      setDragX(0);
      setDragY(0);
    },
    [committedExit],
  );

  useEffect(() => {
    if (!enableKeyboard) return;
    const handleKey = (e: KeyboardEvent): void => {
      if (committedExit) return;
      const tagName = (e.target as HTMLElement | null)?.tagName ?? '';
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tagName)) return;
      if (e.key === 'ArrowLeft') triggerLeft();
      else if (e.key === 'ArrowRight') triggerRight();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [committedExit, triggerLeft, triggerRight, enableKeyboard]);

  const transform =
    committedExit === 'left'
      ? 'translateX(-150vw) rotate(-30deg)'
      : committedExit === 'right'
        ? 'translateX(150vw) rotate(30deg)'
        : // nur nach oben folgen (Math.min(0, …)), Rotation kommt vom horizontalen Drag
          `translate(${dragX}px, ${Math.min(0, dragY)}px) rotate(${dragX * 0.05}deg)`;

  const transition = isDragging
    ? 'none'
    : 'transform 0.22s cubic-bezier(0.4, 0, 0.2, 1)';

  return {
    dragX,
    dragY,
    isDragging,
    committedExit,
    handlers: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel },
    rootRef,
    style: {
      transform,
      transition,
      cursor: isDragging ? 'grabbing' : 'pointer',
      userSelect: 'none',
      willChange: 'transform',
      touchAction: 'none',
    },
  };
}

export { SWIPE_THRESHOLD };
